import { Check, X, Minus } from "lucide-react";
import { cn } from "../lib/utils";

// All features organized by category
const FEATURE_CATEGORIES = [
  {
    name: "Scheduling",
    features: [
      { key: "dayView", label: "Day view scheduling", description: "View and manage daily schedules" },
      { key: "weekView", label: "Week view scheduling", description: "Plan shifts across the week" },
      { key: "dragToCreate", label: "Drag-to-create shifts", description: "Quickly create shifts by dragging" },
      { key: "shiftPatterns", label: "Shift patterns/templates", description: "Save and reuse common shift configurations" },
    ]
  },
  {
    name: "Staff Management",
    features: [
      { key: "employeeProfiles", label: "Employee profiles", description: "Store employee details and preferences" },
      { key: "employeeLimit", label: "Employee limit", description: "Number of employees included", type: "limit", values: { light: "10", standard: "15", professional: "25" } },
      { key: "customColors", label: "Custom employee colors", description: "Color-code employees for easy identification" },
      { key: "employeeAvatars", label: "Employee avatars", description: "Upload profile photos for staff" },
    ]
  },
  {
    name: "Time & Breaks",
    features: [
      { key: "breakManagement", label: "Break management", description: "Configure and track breaks during shifts" },
      { key: "businessHours", label: "Business hours", description: "Set operating hours for your business" },
      { key: "hoursOverrides", label: "Holiday hours", description: "Override hours for special days" },
    ]
  },
  {
    name: "Costs & Reporting",
    features: [
      { key: "wageCostCalculations", label: "Wage cost calculations", description: "Real-time labor cost tracking" },
      { key: "overtimeRates", label: "Custom pay rates", description: "Configure different hourly rates" },
      { key: "basicReports", label: "Basic reports", description: "Essential scheduling reports" },
      { key: "advancedReports", label: "Advanced analytics", description: "Detailed insights and trends" },
      { key: "csvExport", label: "CSV export", description: "Export data to spreadsheets" },
      { key: "pdfExport", label: "PDF export", description: "Generate printable schedules" },
    ]
  },
  {
    name: "Leave & Attendance",
    features: [
      { key: "absenceTracking", label: "Absence tracking", description: "Track sick days, holidays, and leave" },
      { key: "clockInOut", label: "Clock in/out", description: "Time tracking for staff", comingSoon: true },
    ]
  },
  {
    name: "Advanced",
    features: [
      { key: "multipleLocations", label: "Multiple locations", description: "Manage schedules across locations", comingSoon: true },
      { key: "multipleDepartments", label: "Multiple departments", description: "Organize staff by department", comingSoon: true },
      { key: "payrollIntegrations", label: "Payroll integrations", description: "Connect to Sage, ADP, and more", comingSoon: true },
    ]
  }
];

// Default feature availability per tier
const TIER_FEATURES = {
  light: {
    dayView: true,
    weekView: true,
    dragToCreate: true,
    shiftPatterns: true,
    employeeProfiles: true,
    customColors: true,
    employeeAvatars: true,
    businessHours: true,
    hoursOverrides: true,
    basicReports: true,
    // Not included in Light
    breakManagement: false,
    wageCostCalculations: false,
    overtimeRates: false,
    advancedReports: false,
    csvExport: false,
    pdfExport: false,
    absenceTracking: false,
    clockInOut: false,
    multipleLocations: false,
    multipleDepartments: false,
    payrollIntegrations: false,
  },
  standard: {
    dayView: true,
    weekView: true,
    dragToCreate: true,
    shiftPatterns: true,
    employeeProfiles: true,
    customColors: true,
    employeeAvatars: true,
    businessHours: true,
    hoursOverrides: true,
    basicReports: true,
    breakManagement: true,
    wageCostCalculations: true,
    overtimeRates: true,
    advancedReports: true,
    csvExport: true,
    pdfExport: true,
    absenceTracking: true,
    // Not included in Standard
    clockInOut: false,
    multipleLocations: false,
    multipleDepartments: false,
    payrollIntegrations: false,
  },
  professional: {
    dayView: true,
    weekView: true,
    dragToCreate: true,
    shiftPatterns: true,
    employeeProfiles: true,
    customColors: true,
    employeeAvatars: true,
    businessHours: true,
    hoursOverrides: true,
    basicReports: true,
    breakManagement: true,
    wageCostCalculations: true,
    overtimeRates: true,
    advancedReports: true,
    csvExport: true,
    pdfExport: true,
    absenceTracking: true,
    clockInOut: true,
    multipleLocations: true,
    multipleDepartments: true,
    payrollIntegrations: true,
  }
};

const FeatureCell = ({ included, value, comingSoon }) => {
  if (comingSoon) {
    return (
      <span className="text-xs text-slate-400 font-medium">Soon</span>
    );
  }
  
  if (value) {
    return <span className="text-sm font-semibold text-slate-900">{value}</span>;
  }
  
  if (included) {
    return <Check className="w-5 h-5 text-teal-500 mx-auto" />;
  }
  
  return <Minus className="w-5 h-5 text-slate-300 mx-auto" />;
};

const PlanComparisonTable = ({ 
  variant = "full", // "full" | "compact"
  packages = [],
  currencySymbol = "£",
  showPrices = true,
  className = "",
  highlightTier = "standard", // Which tier to highlight
}) => {
  // Determine tiers to show based on packages or default
  const tiers = packages.length > 0 
    ? packages.map(p => ({ 
        id: p.tier, 
        name: p.name, 
        price: p.price,
        featureFlags: p.feature_flags || TIER_FEATURES[p.tier] || {}
      }))
    : [
        { id: "light", name: "Light", price: null, featureFlags: TIER_FEATURES.light },
        { id: "standard", name: "Standard", price: null, featureFlags: TIER_FEATURES.standard },
        { id: "professional", name: "Professional", price: null, featureFlags: TIER_FEATURES.professional }
      ];

  return (
    <div className={cn("overflow-x-auto", className)}>
      <table className="w-full border-collapse">
        {/* Header */}
        <thead>
          <tr>
            <th className="text-left py-4 px-4 font-semibold text-slate-900 bg-slate-50 rounded-tl-lg min-w-[200px]">
              Features
            </th>
            {tiers.map((tier) => (
              <th 
                key={tier.id} 
                className={cn(
                  "py-4 px-6 text-center min-w-[140px]",
                  tier.id === highlightTier 
                    ? "bg-gradient-to-b from-violet-500 to-violet-600 text-white rounded-t-lg" 
                    : "bg-slate-50"
                )}
              >
                <div className="font-semibold">{tier.name}</div>
                {showPrices && tier.price !== null && (
                  <div className={cn(
                    "text-sm mt-1",
                    tier.id === highlightTier ? "text-white/80" : "text-slate-500"
                  )}>
                    {currencySymbol}{tier.price}/mo
                  </div>
                )}
              </th>
            ))}
          </tr>
        </thead>
        
        {/* Body */}
        <tbody>
          {FEATURE_CATEGORIES.map((category, catIndex) => (
            <>
              {/* Category Header */}
              <tr key={`cat-${catIndex}`}>
                <td 
                  colSpan={tiers.length + 1} 
                  className="py-3 px-4 bg-slate-100/50 font-semibold text-slate-700 text-sm uppercase tracking-wide"
                >
                  {category.name}
                </td>
              </tr>
              
              {/* Feature Rows */}
              {category.features.map((feature, featureIndex) => (
                <tr 
                  key={`${catIndex}-${featureIndex}`}
                  className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
                >
                  <td className="py-3 px-4">
                    <div className="font-medium text-slate-900 text-sm">{feature.label}</div>
                    {variant === "full" && feature.description && (
                      <div className="text-xs text-slate-500 mt-0.5">{feature.description}</div>
                    )}
                  </td>
                  {tiers.map((tier) => {
                    const isHighlight = tier.id === highlightTier;
                    const included = tier.featureFlags[feature.key] || TIER_FEATURES[tier.id]?.[feature.key];
                    const limitValue = feature.type === "limit" ? feature.values?.[tier.id] : null;
                    
                    return (
                      <td 
                        key={tier.id} 
                        className={cn(
                          "py-3 px-6 text-center",
                          isHighlight && "bg-violet-50/50"
                        )}
                      >
                        <FeatureCell 
                          included={included} 
                          value={limitValue}
                          comingSoon={feature.comingSoon}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </>
          ))}
        </tbody>
      </table>
      
      {/* Legend */}
      <div className="flex items-center justify-center gap-6 mt-6 text-xs text-slate-500">
        <div className="flex items-center gap-2">
          <Check className="w-4 h-4 text-teal-500" />
          <span>Included</span>
        </div>
        <div className="flex items-center gap-2">
          <Minus className="w-4 h-4 text-slate-300" />
          <span>Not included</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-slate-400 font-medium">Soon</span>
          <span>Coming soon</span>
        </div>
      </div>
    </div>
  );
};

export default PlanComparisonTable;
